import { queryOptions } from "@tanstack/react-query";

import { api, mBrandResearchBaseURL } from "@/config/api";
import {
  AdsIntelligenceOverviewSchema,
  AdsIntelligenceInsightsSchema,
  AdsIntelligenceTrendsSchema,
  ScheduleResponseSchema,
  ScheduleListResponseSchema,
  ReportListResponseSchema,
  ReportItemSchema,
} from "@/schema/brand-os/paid-media";
import { createQueryString, createURLPath } from "@/lib/utils";
import { unwrapApiError } from "@/lib/error";

interface FetchAdsOverviewParams extends Record<string, string | number | boolean | null | undefined> {
  date_range_start: string;
  date_range_end: string;
  campaign_id?: string | null;
  adset_id?: string | null;
  product?: string | null;
}

interface FetchSchedulesParams extends Record<string, string | number | boolean | null | undefined> {
  organization_id: string;
}

interface ScheduleActionParams {
  schedule_id: string;
  organization_id: string;
}

interface ScheduleBody {
  name: string;
  prompt: string;
  frequency: string;
  time_of_day: string;
  day_config: Record<string, unknown> | null;
  date_range: string;
  comparison: string;
  notify_email: boolean;
  email_recipients: string[];
  notify_slack: boolean;
  notify_in_app: boolean;
  ad_account_id: string;
  source: string;
}

interface ScheduleMutationParams {
  schedule_id?: string;
  organization_id: string;
  user_id?: string;
  body: ScheduleBody;
}

interface FetchReportsParams extends Record<string, string | number | boolean | null | undefined> {
  organization_id: string;
  filter?: "all" | "read" | "unread";
  schedule_id?: string | null;
  page?: number;
  limit?: number;
}

interface FetchReportParams {
  report_id: string;
  organization_id: string;
}

interface MarkReportReadParams {
  report_id: string;
  organization_id: string;
}

export const PaidMediaApiFactory = {
  Endpoint: mBrandResearchBaseURL + "/api/v1",
  ReportsEndpoint: mBrandResearchBaseURL + "/api/v1/reports",
  Keys: {
    FetchOverview: (params: FetchAdsOverviewParams) => ["paid-media", "overview", createQueryString(params)],
    FetchInsights: (params: FetchAdsOverviewParams) => ["paid-media", "insights", createQueryString(params)],
    FetchTrends: (params: FetchAdsOverviewParams) => ["paid-media", "trends", createQueryString(params)],
    FetchSchedules: (params: FetchSchedulesParams) => ["paid-media", "schedules", createQueryString(params)],
    FetchSchedule: (params: ScheduleActionParams) => ["paid-media", "schedule", params.schedule_id, createQueryString({ organization_id: params.organization_id })],
    FetchReports: (params: FetchReportsParams) => ["paid-media", "reports", createQueryString(params)],
    FetchReport: (params: FetchReportParams) => ["paid-media", "report", params.report_id, params.organization_id],
  },
  Schemas: {
    Response: {
      Overview: AdsIntelligenceOverviewSchema,
      Insights: AdsIntelligenceInsightsSchema,
      Trends: AdsIntelligenceTrendsSchema,
      Schedule: ScheduleResponseSchema,
      Schedules: ScheduleListResponseSchema,
    },
  },
  Apis: {
    FetchOverview: async (params: FetchAdsOverviewParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "overview") + "?" + createQueryString(params));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(PaidMediaApiFactory.Schemas.Response.Overview.parse);
    },

    FetchInsights: async (params: FetchAdsOverviewParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "insights") + "?" + createQueryString(params));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(PaidMediaApiFactory.Schemas.Response.Insights.parse);
    },

    FetchTrends: async (params: FetchAdsOverviewParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "trends") + "?" + createQueryString(params));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(PaidMediaApiFactory.Schemas.Response.Trends.parse);
    },

    FetchScheduleList: async (params: FetchSchedulesParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "schedules") + "?" + createQueryString(params));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(PaidMediaApiFactory.Schemas.Response.Schedules.parse);
    },

    FetchSchedule: async ({ schedule_id, organization_id }: ScheduleActionParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "schedules", schedule_id) + "?" + createQueryString({ organization_id }));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(PaidMediaApiFactory.Schemas.Response.Schedule.parse);
    },

    UpdateSchedule: async ({ schedule_id, organization_id, body }: ScheduleMutationParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "schedules", schedule_id!) + "?" + createQueryString({ organization_id }), {
        method: "PUT",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }
    },

    CreateSchedule: async ({ organization_id, user_id, body }: ScheduleMutationParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "schedules") + "?" + createQueryString({ organization_id, user_id }), {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }
    },

    PauseSchedule: async ({ schedule_id, organization_id }: ScheduleActionParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "schedules", schedule_id, "pause") + "?" + createQueryString({ organization_id }), {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }
    },

    ResumeSchedule: async ({ schedule_id, organization_id }: ScheduleActionParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "schedules", schedule_id, "resume") + "?" + createQueryString({ organization_id }), {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }
    },

    DeleteSchedule: async ({ schedule_id, organization_id }: ScheduleActionParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.Endpoint, "ads-intelligence", "schedules", schedule_id) + "?" + createQueryString({ organization_id }), {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }
    },

    FetchReports: async (params: FetchReportsParams) => {
      const response = await api(PaidMediaApiFactory.ReportsEndpoint + "?" + createQueryString(params));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ReportListResponseSchema.parse);
    },

    FetchReport: async ({ report_id, organization_id }: FetchReportParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.ReportsEndpoint, report_id) + "?" + createQueryString({ organization_id }));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ReportItemSchema.parse);
    },

    MarkReportRead: async ({ report_id, organization_id }: MarkReportReadParams) => {
      const response = await api(createURLPath(PaidMediaApiFactory.ReportsEndpoint, report_id, "read") + "?" + createQueryString({ organization_id }), {
        method: "PATCH",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }
    },
  },
  Queries: {
    FetchOverview: (params: FetchAdsOverviewParams) =>
      queryOptions({
        queryKey: PaidMediaApiFactory.Keys.FetchOverview(params),
        queryFn: () => PaidMediaApiFactory.Apis.FetchOverview(params),
      }),

    FetchInsights: (params: FetchAdsOverviewParams) =>
      queryOptions({
        queryKey: PaidMediaApiFactory.Keys.FetchInsights(params),
        queryFn: () => PaidMediaApiFactory.Apis.FetchInsights(params),
      }),

    FetchTrends: (params: FetchAdsOverviewParams) =>
      queryOptions({
        queryKey: PaidMediaApiFactory.Keys.FetchTrends(params),
        queryFn: () => PaidMediaApiFactory.Apis.FetchTrends(params),
      }),

    FetchSchedules: (params: FetchSchedulesParams) =>
      queryOptions({
        queryKey: PaidMediaApiFactory.Keys.FetchSchedules(params),
        queryFn: () => PaidMediaApiFactory.Apis.FetchScheduleList(params),
        enabled: !!params.organization_id,
      }),

    FetchSchedule: (params: ScheduleActionParams) =>
      queryOptions({
        queryKey: PaidMediaApiFactory.Keys.FetchSchedule(params),
        queryFn: () => PaidMediaApiFactory.Apis.FetchSchedule(params),
        enabled: !!params.schedule_id && !!params.organization_id,
      }),

    FetchReports: (params: FetchReportsParams) =>
      queryOptions({
        queryKey: PaidMediaApiFactory.Keys.FetchReports(params),
        queryFn: () => PaidMediaApiFactory.Apis.FetchReports(params),
        enabled: !!params.organization_id,
      }),

    FetchReport: (params: FetchReportParams) =>
      queryOptions({
        queryKey: PaidMediaApiFactory.Keys.FetchReport(params),
        queryFn: () => PaidMediaApiFactory.Apis.FetchReport(params),
        enabled: !!params.report_id && !!params.organization_id,
      }),
  },
};
